// @ts-nocheck

import { debounce } from 'lodash';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ResizeDetector from 'react-resize-detector';
import styled, { css } from 'styled-components';

import { ReactComponent as Close } from '../../assets/svg/close.svg';
import { ReactComponent as Plus } from '../../assets/svg/plus.svg';
import { Target } from '../../client';
import { MultiviewStyles } from '../../constants/multiview';
import { MultiviewMatchContext } from '../../contexts/MultiviewMatchRouteContext';
import useRouter from '../../hooks/useRouter';
import { Button, MultiviewBlockWrapper } from '../../styled-components/shared';
import { toPercent } from '../../styled-components/utils';
import { MultiviewDisplayModes, type MultiviewDisplayMode } from '../../types/multiview';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import FloatingWtvPlaceholder from '../FloatingWtvPlaceholder';
import MultiviewBlockAddMatch from '../MultiviewBlockAddMatch';
import MultiviewBlockMatchContainer from '../MultiviewBlockMatch/MultiviewBlockMatchContainer';

const AddButton = styled(Button)`
  && {
    background-color: ${({ theme }) => theme.common.colors.primary};
    height: 2.5em;
    width: 2.5em;
    border-radius: 50%;
    padding: 0;

    position: absolute;
    left: 1em;
    left: calc(constant(safe-area-inset-left) + 1em);
    left: calc(env(safe-area-inset-left) + 1em);
    bottom: 1em;
    bottom: calc(constant(safe-area-inset-bottom) + 1em);
    bottom: calc(env(safe-area-inset-bottom) + 1em);
    svg {
      fill: #fff;
      height: 1.2em;
      width: 1.2em;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);
    }
  }
`;

const ListCardWrapper = styled.div`
  position: relative;
  padding: ${Target.__MOBILE__ ? '20px 5px 5px' : '0 0 20px 5px'};
  box-sizing: border-box;

  ${({ $height }) =>
    $height
      ? css`
          height: ${$height}px;
        `
      : css`
          height: 80vh;
          min-height: 650px;
        `};
`;

const widthDesktop = css`
  width: ${({ $mode, $itemPerLine }) =>
    $mode === MultiviewDisplayModes.EXPANDED ? toPercent(1 / $itemPerLine) : '100%'};
`;

const ListWrapper = styled.div`
  ::after {
    display: block;
    content: ' ';
    clear: both;
  }
`;

const ListItemWrapper = styled.div`
  float: left;
  box-sizing: border-box;

  ${Target.__MOBILE__
    ? css`
        width: 100%;
      `
    : css`
        min-width: ${MultiviewStyles.MIN_WIDTH_CARD}px;
        ${widthDesktop}
      `}
`;
const ListItem = styled.div`
  width: 100%;
  box-sizing: border-box;
`;
const Dots = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
`;
const Dot = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background: ${({ $isSelected, theme }) => ($isSelected ? theme.common.colors.dark : theme.common.colors.headerColor)};
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 13;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  background-color: ${({ theme }) => theme.common.colors.lighter};
`;

const DeleteIcon = styled(Close)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  transform: translate3d(-50%, -50%, 0);
  background: transparent;

  ${Target.__MOBILE__
    ? undefined
    : css`
        &:hover {
          color: ${({ theme }) => theme.common.colors.dark};
        }
      `}
`;

const PlusIcon = styled(Plus)`
  color: ${({ theme }) => theme.common.colors.mild};
`;

type MultiviewBlockListProps = {
  list: Array<number>;
  itemPerLine: number;
  userOption: number;
  isWTVEnabled: boolean;
  showWTV: boolean;
  mode: MultiviewDisplayMode;
  height?: number;
  setDisplayOptions: (...params: unknown) => unknown;
  setShowWinamaxTv: (...params: unknown) => unknown;
};

const MultiviewBlockList = ({
  list,
  itemPerLine,
  userOption,
  isWTVEnabled,
  showWTV,
  mode,
  height,
  setDisplayOptions,
  setShowWinamaxTv,
}: MultiviewBlockListProps) => {
  const { history } = useRouter();

  const [swipePosition, setSwipePosition] = useState(0);

  const bestItemPerLine = userOption > itemPerLine ? itemPerLine : userOption;

  useEffect(() => {
    if (swipePosition > list.length - 1) {
      // We are not apply the -1 because we have a static item
      setSwipePosition(list.length);
    }
  }, [list.length, swipePosition]);

  useEffect(() => {
    setSwipePosition(0);
  }, [isWTVEnabled]);

  const handleSetShowMatchList = useCallback(() => {
    history.push(RoutesRedirectionFactory.getMultiviewEventListRouteConfig());
  }, [history]);

  const onResize = useCallback(
    (width) => {
      if (!Target.__MOBILE__) {
        setDisplayOptions(width);
      }
    },
    [setDisplayOptions],
  );

  const handleClickClose = useCallback(() => {
    setShowWinamaxTv(!showWTV);
  }, [setShowWinamaxTv, showWTV]);

  const swipeOptions = useMemo(
    () => ({
      startSlide: swipePosition,
      continuous: false,
      auto: 0,
      callback: setSwipePosition,
    }),
    [swipePosition],
  );

  const isWTVvisible = isWTVEnabled && showWTV;
  const cProps = Target.__MOBILE__ ? { swipeOptions, childCount: list.length + (isWTVvisible ? 1 : 0) } : {};

  return (
    <React.Fragment>
      <ListWrapper {...cProps}>
        {isWTVvisible && (
          <ListItemWrapper $mode={mode} $itemPerLine={bestItemPerLine}>
            <ListItem>
              <ListCardWrapper $height={height}>
                <MultiviewBlockWrapper>
                  <CloseButton onClick={handleClickClose}>
                    <DeleteIcon />
                  </CloseButton>
                  <FloatingWtvPlaceholder />
                </MultiviewBlockWrapper>
              </ListCardWrapper>
            </ListItem>
          </ListItemWrapper>
        )}
        {list.map((matchId, index) => {
          return (
            <MultiviewMatchContext.Provider key={matchId} value={{ matchId }}>
              <ListItemWrapper key={matchId} $mode={mode} $itemPerLine={bestItemPerLine}>
                <ListItem>
                  <ListCardWrapper $height={height}>
                    <MultiviewBlockMatchContainer
                      visible={
                        !Target.__MOBILE__ || (Target.__MOBILE__ && swipePosition === index + (isWTVvisible ? 1 : 0))
                      }
                    />
                  </ListCardWrapper>
                </ListItem>
              </ListItemWrapper>
            </MultiviewMatchContext.Provider>
          );
        })}
        <ListItemWrapper $mode={mode} $itemPerLine={bestItemPerLine}>
          <ListItem>
            <ListCardWrapper $height={height}>
              <MultiviewBlockAddMatch />
            </ListCardWrapper>
          </ListItem>
        </ListItemWrapper>
      </ListWrapper>
      <ResizeDetector handleWidth onResize={debounce(onResize, 500)} />
      {Target.__MOBILE__ ? (
        <div>
          {swipePosition !== list.length + (isWTVvisible ? 1 : 0) &&
          ((isWTVvisible && swipePosition) || !isWTVvisible) ? (
            <AddButton onClick={handleSetShowMatchList}>
              <PlusIcon />
            </AddButton>
          ) : null}
          <Dots>
            <Dot
              style={{
                width: `${100 / (list.length + 1 + (isWTVvisible ? 1 : 0))}%`,
                transform: `translateX(${100 * swipePosition}%)`,
              }}
              $isSelected
            />
          </Dots>
        </div>
      ) : null}
    </React.Fragment>
  );
};

export default MultiviewBlockList;
